import type { FormEvent, ReactNode } from 'react';
import type { IntegrationConnectorType } from '@marketing-hub/shared';
import { Button } from '@/components/ui/Button';
import { ConnectorTypePicker } from './ConnectorTypePicker';
import styles from './IntegrationSetupWizard.module.css';

type Props = {
  open: boolean;
  onClose: () => void;
  connectorType: IntegrationConnectorType;
  onConnectorTypeChange: (t: IntegrationConnectorType) => void;
  stepCount: number;
  configStep: ReactNode;
  credentialsStep: ReactNode;
  notice: string | null;
  error: ReactNode;
  submitting: boolean;
  canSubmit: boolean;
  onSubmit: (e: FormEvent) => void;
};

export function IntegrationSetupWizard({
  open,
  onClose,
  connectorType,
  onConnectorTypeChange,
  stepCount,
  configStep,
  credentialsStep,
  notice,
  error,
  submitting,
  canSubmit,
  onSubmit,
}: Props) {
  if (!open) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="integration-wizard-title">
      <button type="button" className={styles.backdrop} aria-label="Sluiten" onClick={onClose} />
      <div className={styles.panel}>
        <header className={styles.header}>
          <div>
            <h2 id="integration-wizard-title" className={styles.title}>
              Koppeling toevoegen
            </h2>
            <p className={styles.subtitle}>Credentials worden versleuteld opgeslagen. Eén connector per platform.</p>
          </div>
          <Button type="button" variant="ghost" onClick={onClose}>
            Sluiten
          </Button>
        </header>

        <ol className={styles.progress} aria-label="Stappen">
          <li className={styles.progressItem} data-active>
            <span className={styles.progressDot}>1</span>
            Platform
          </li>
          <li className={styles.progressLine} aria-hidden />
          <li className={styles.progressItem} data-active={stepCount >= 2 ? true : undefined}>
            <span className={styles.progressDot}>2</span>
            Config
          </li>
          <li className={styles.progressLine} aria-hidden />
          <li className={styles.progressItem} data-active>
            <span className={styles.progressDot}>{stepCount >= 3 ? 3 : 2}</span>
            Credentials
          </li>
        </ol>

        <form className={styles.form} onSubmit={(e) => void onSubmit(e)}>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Kies platform</h3>
            <div className={styles.pickerWrap}>
              <ConnectorTypePicker value={connectorType} onChange={onConnectorTypeChange} />
            </div>
          </section>

          {configStep ? (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Configuratie</h3>
              {configStep}
            </section>
          ) : null}

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Authenticatie</h3>
            {credentialsStep}
          </section>

          {notice ? <p className={styles.notice}>{notice}</p> : null}
          {error}

          <footer className={styles.footer}>
            <Button type="button" variant="ghost" onClick={onClose}>
              Annuleren
            </Button>
            <Button type="submit" disabled={submitting || !canSubmit}>
              {submitting ? 'Bezig…' : 'Koppelen & sync starten'}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
